import { lazy, Suspense, type ReactNode } from "react";
import type { RouteRecord } from "vite-react-ssg";
import { Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { ProtectedAdminRoute } from "@/components/admin/ProtectedAdminRoute";
import { AuthProvider } from "@/contexts/AuthContext";
import AppErrorBoundary from "@/components/system/AppErrorBoundary";
import { supabase } from "@/integrations/supabase/client";
import communityData from "@/data/communityData";
import { loader as agentProfileLoader } from "./pages/agent/AgentProfile";
import { loader as blogPostLoader } from "./pages/BlogPost";

const PageViewTracker = lazy(() => import("@/components/tracking/PageViewTracker"));
const RetargetingPixels = lazy(() => import("@/components/tracking/RetargetingPixels"));

// Loading fallback used during client-side lazy imports
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
  </div>
);

// Single QueryClient instance for the app
const queryClient = new QueryClient();

// Root layout — wraps every route with providers. Equivalent to the old
// <App> tree but as a React Router root route so vite-react-ssg can render it.
const RootLayout = ({ children }: { children?: ReactNode }) => (
  <AppErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AuthProvider>
            <Suspense fallback={null}>
              <RetargetingPixels />
              <PageViewTracker />
            </Suspense>
            <Suspense fallback={<PageLoader />}>
              {children ?? <Outlet />}
            </Suspense>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </AppErrorBoundary>
);

// EAGER imports for SEO-critical routes that are pre-rendered to static HTML.
// React.lazy() suspends during SSG serialization, producing empty HTML for
// the route's component. Eager imports guarantee the component renders into
// the prerendered string. Code-splitting these routes provides no benefit
// because each route is its own HTML file with its own JS chunk anyway.
import Index from "./pages/Index";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Sell from "./pages/Sell";
import Agents from "./pages/Agents";
import Sold from "./pages/Sold";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import AgentProfile from "./pages/agent/AgentProfile";
import Privacy from "./pages/legal/Privacy";
import Terms from "./pages/legal/Terms";
import FairHousing from "./pages/legal/FairHousing";
import Accessibility from "./pages/legal/Accessibility";
import FirstTimeHomeBuyer from "./pages/resources/FirstTimeHomeBuyer";
import ClosingCostsNJ from "./pages/resources/ClosingCostsNJ";
import HomeValue from "./pages/resources/HomeValue";
import HowToBuyHouseNJ from "./pages/resources/HowToBuyHouseNJ";
import Careers from "./pages/resources/Careers";
import Communities from "./pages/resources/Communities";
import CommunityPage from "./pages/CommunityPage";
import BuyerLandingPage from "./pages/landing/BuyerLandingPage";
import SellerLandingPage from "./pages/landing/SellerLandingPage";
import MoorestownLanding from "./pages/landing/MoorestownLanding";
import MountLaurelLanding from "./pages/landing/MountLaurelLanding";
import VoorheesLanding from "./pages/landing/VoorheesLanding";
import HaddonfieldLanding from "./pages/landing/HaddonfieldLanding";
import BlackwoodLanding from "./pages/landing/BlackwoodLanding";
import MarltonLanding from "./pages/landing/MarltonLanding";
import MedfordLanding from "./pages/landing/MedfordLanding";
import CherryHillLanding from "./pages/landing/CherryHillLanding";
import SicklervilleLanding from "./pages/landing/SicklervilleLanding";
import HouseHackingLanding from "./pages/landing/HouseHackingLanding";
import NotFound from "./pages/NotFound";

// Lazy imports — only for client-only routes (auth-gated, dynamic, admin).
const ListingDetail = lazy(() => import("./pages/ListingDetail"));
const Auth = lazy(() => import("./pages/Auth"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const AgentDashboard = lazy(() => import("./pages/agent/AgentDashboard"));
const OpenHouseDashboard = lazy(() => import("./pages/agent/OpenHouseDashboard"));
const AgentProfileEdit = lazy(() => import("./pages/agent/AgentProfileEdit"));
const OpenHouseSignIn = lazy(() => import("./pages/OpenHouseSignIn"));
const LandingPage = lazy(() => import("./pages/landing/LandingPage"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AgentManagement = lazy(() => import("./pages/admin/AgentManagement"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const BlogAdmin = lazy(() => import("./pages/admin/BlogAdmin"));
const BlogEditor = lazy(() => import("./pages/admin/BlogEditor"));
const ClientPortal = lazy(() => import("./pages/client/ClientPortal"));
const SandboxIDXBrokerTestPage = lazy(() => import("./pages/SandboxIDXBrokerTestPage"));

// Helper: wrap an element with a Suspense fallback (router data routes need
// it because lazy components must suspend somewhere).
const withSuspense = (node: ReactNode) => (
  <Suspense fallback={<PageLoader />}>{node}</Suspense>
);

// All routes. Routes that should be pre-rendered to static HTML have
// `entry: true` (default for static paths). Auth-gated, dynamic, or
// non-SEO routes set `entry: false` to remain client-only.
//
// Dynamic routes use `getStaticPaths` to enumerate every concrete path
// at build time (Supabase fetch via anon key).
export const routes: RouteRecord[] = [
  {
    path: "/",
    element: <RootLayout />,
    children: [
      // ---------- Static, pre-rendered ----------
      { index: true, element: withSuspense(<Index />) },
      { path: "about", element: withSuspense(<About />) },
      { path: "contact", element: withSuspense(<Contact />) },
      { path: "sell", element: withSuspense(<Sell />) },
      { path: "agents", element: withSuspense(<Agents />) },
      { path: "sold", element: withSuspense(<Sold />) },
      { path: "blog", element: withSuspense(<Blog />) },
      { path: "privacy", element: withSuspense(<Privacy />) },
      { path: "terms", element: withSuspense(<Terms />) },
      { path: "fair-housing", element: withSuspense(<FairHousing />) },
      { path: "accessibility", element: withSuspense(<Accessibility />) },
      { path: "first-time-home-buyer-nj", element: withSuspense(<FirstTimeHomeBuyer />) },
      { path: "closing-costs-nj", element: withSuspense(<ClosingCostsNJ />) },
      { path: "home-value", element: withSuspense(<HomeValue />) },
      { path: "how-to-buy-a-house-nj", element: withSuspense(<HowToBuyHouseNJ />) },
      { path: "careers", element: withSuspense(<Careers />) },
      { path: "communities", element: withSuspense(<Communities />) },
      // PPC landing pages
      { path: "homes-for-sale-south-jersey", element: withSuspense(<BuyerLandingPage />) },
      { path: "homes-for-sale-moorestown", element: withSuspense(<MoorestownLanding />) },
      { path: "homes-for-sale-mount-laurel", element: withSuspense(<MountLaurelLanding />) },
      { path: "homes-for-sale-voorhees", element: withSuspense(<VoorheesLanding />) },
      { path: "homes-for-sale-haddonfield", element: withSuspense(<HaddonfieldLanding />) },
      { path: "homes-for-sale-blackwood", element: withSuspense(<BlackwoodLanding />) },
      { path: "homes-for-sale-marlton", element: withSuspense(<MarltonLanding />) },
      { path: "homes-for-sale-medford", element: withSuspense(<MedfordLanding />) },
      { path: "homes-for-sale-cherry-hill", element: withSuspense(<CherryHillLanding />) },
      { path: "homes-for-sale-sicklerville", element: withSuspense(<SicklervilleLanding />) },
      { path: "multifamily-homes-for-sale-nj", element: withSuspense(<HouseHackingLanding />) },
      { path: "what-is-my-home-worth", element: withSuspense(<SellerLandingPage />) },

      // ---------- Dynamic, pre-rendered via getStaticPaths ----------
      {
        path: "agent/:slug",
        element: withSuspense(<AgentProfile />),
        loader: agentProfileLoader,
        getStaticPaths: async () => {
          // Use agents_public view — no RLS restriction for anon key at build time.
          const { data, error } = await supabase
            .from("agents_public")
            .select("slug")
            .eq("is_active", true)
            .not("slug", "is", null);
          if (error) {
            console.error("[SSG] failed to enumerate agents:", error);
            return [];
          }
          const slugs = (data ?? [])
            .map((r) => r.slug)
            .filter((s): s is string => !!s);
          console.log(`[SSG] enumerated ${slugs.length} agent slugs`);
          return slugs.map((slug) => `/agent/${slug}`);
        },
      },
      {
        path: "blog/:slug",
        element: withSuspense(<BlogPost />),
        loader: blogPostLoader,
        getStaticPaths: async () => {
          const { data, error } = await supabase
            .from("blog_posts")
            .select("slug")
            .eq("status", "published");
          if (error) {
            console.error("[SSG] failed to enumerate blog posts:", error);
            return [];
          }
          const slugs = (data ?? [])
            .map((r) => r.slug)
            .filter((s): s is string => !!s);
          console.log(`[SSG] enumerated ${slugs.length} blog post slugs`);
          return slugs.map((slug) => `/blog/${slug}`);
        },
      },

      // ---------- Client-only (SSG skipped) ----------
      { path: "auth", element: withSuspense(<Auth />) },
      { path: "settings", element: withSuspense(<SettingsPage />) },
      { path: "agent", element: withSuspense(<AgentDashboard />) },
      { path: "agent/open-house", element: withSuspense(<OpenHouseDashboard />) },
      { path: "agent/profile/edit", element: withSuspense(<AgentProfileEdit />) },
      { path: "open-house/:sessionCode", element: withSuspense(<OpenHouseSignIn />) },
      { path: "listing/:id", element: withSuspense(<ListingDetail />) },
      { path: "lp/:slug", element: withSuspense(<LandingPage />) },
      { path: "homes-for-sale-in-:location", element: withSuspense(<LandingPage />) },
      { path: "homes-for-sale-:zip", element: withSuspense(<LandingPage />) },
      { path: "whats-my-home-worth-in-:location", element: withSuspense(<LandingPage />) },
      { path: "client/:portalId", element: withSuspense(<ClientPortal />) },
      { path: "sandbox-idx-broker-test-page", element: withSuspense(<SandboxIDXBrokerTestPage />) },

      // Admin (protected, client-only)
      {
        path: "admin",
        element: <ProtectedAdminRoute>{withSuspense(<AdminDashboard />)}</ProtectedAdminRoute>,
      },
      {
        path: "admin/agents",
        element: <ProtectedAdminRoute>{withSuspense(<AgentManagement />)}</ProtectedAdminRoute>,
      },
      {
        path: "admin/users",
        element: <ProtectedAdminRoute>{withSuspense(<UserManagement />)}</ProtectedAdminRoute>,
      },
      { path: "admin/blog", element: withSuspense(<BlogAdmin />) },
      { path: "admin/blog/new", element: withSuspense(<BlogEditor />) },
      { path: "admin/blog/edit/:id", element: withSuspense(<BlogEditor />) },

      // ---------- Community pages (dynamic slug catch — pre-rendered) ----------
      {
        path: ":slug",
        element: withSuspense(<CommunityPage />),
        getStaticPaths: () => {
          // Enumerate from the static communityData map (no DB call needed).
          const slugs = Object.keys(communityData);
          console.log(`[SSG] enumerated ${slugs.length} community slugs`);
          return slugs.map((slug) => `/${slug}`);
        },
      },

      // 404 fallback (client-only — Lovable hosting serves index.html for unknown paths)
      { path: "*", element: withSuspense(<NotFound />) },
    ],
  },
];
